import React from 'react';
import { useWebRTC } from '../../hooks/useWebRTC';
import { useConference } from '../../hooks/useConference';
import { WebRTCContext } from './WebRTCContext';
import IncomingCallModal from './IncomingCallModal';
import IncomingHandoffModal from './IncomingHandoffModal';
import ActiveCallInterface from './ActiveCallInterface';
import ConferenceUI from './ConferenceUI';

// The ONLY component that instantiates useWebRTC(). It owns the single signaling socket and
// shares everything via WebRTCContext. The global call UI (incoming ring + active call) is
// rendered here so calls work on any page, with real staff name/role via resolveStaff.
const WebRTCProvider = ({ children }) => {
  const rtc = useWebRTC();
  const conf = useConference(rtc);          // staff audio conference (mesh) — shares the one signaling socket
  const ctxValue = { ...rtc, ...conf };

  return (
    <WebRTCContext.Provider value={ctxValue}>
      {children}

      {/* Global WebRTC call UI (single instance) */}
      <IncomingCallModal
        incomingCall={rtc.incomingCall}
        onAccept={rtc.acceptCall}
        onReject={rtc.rejectCall}
        resolveStaff={rtc.resolveStaff}
      />

      {/* P0-D: global customer-handoff alert — rings/appears on EVERY authenticated Business route so an
          eligible operator is never unreachable off the Operator Workspace page. */}
      <IncomingHandoffModal
        incomingHandoff={rtc.incomingHandoff}
        onAccept={rtc.acceptIncomingHandoff}
        onDismiss={rtc.dismissIncomingHandoff}
      />

      {/* Global OPERATOR-CALL ring (scheduled encounter / AI-Voice) for an operator who is not on the
          Operator Workspace. Answer calls the SAME extracted canonical accept the Workspace uses, IN
          PLACE — no navigation, because the trusted click is what grants audio autoplay. Same component
          as the handoff alert (no second call screen); it hides itself on /unified-desktop. */}
      <IncomingHandoffModal
        incomingHandoff={rtc.operatorCallRing}
        onAccept={rtc.acceptOperatorCallRing}
        onDismiss={() => { try { rtc.claimOperatorCallRing(); } catch (e) { /* already cleared */ } }}
      />

      <ActiveCallInterface
        currentCall={rtc.currentCall}
        callStatus={rtc.callStatus}
        isOnHold={rtc.isOnHold}
        localVideoRef={rtc.localVideoRef}
        remoteVideoRef={rtc.remoteVideoRef}
        remoteStream={rtc.remoteStream}
        resolveStaff={rtc.resolveStaff}
        onEndCall={rtc.endCall}
        onToggleMute={rtc.toggleMute}
        onToggleVideo={rtc.toggleVideo}
        onToggleHold={rtc.toggleHold}
        onScreenShare={rtc.startScreenShare}
        diag={rtc.diag}
        iceInfo={rtc.iceInfo}
        wsReconnects={rtc.wsReconnects}
      />

      {/* Global staff audio conference UI (incoming invite + active panel) */}
      <ConferenceUI />
    </WebRTCContext.Provider>
  );
};

export default WebRTCProvider;
